import { createSlice } from '@reduxjs/toolkit'
import {
  fetchUserConversations,
  fetchConversationMessages,
  sendChatMessage,
  markConversationAsRead,
  fetchChatLogUsers,
  loadChatConversation,
  exportChatLogs,
  getGDPRConsents,
  updateGDPRConsents,
} from '../actions/chatActions.js'

const initialState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  loading: false,
  error: null,
  // Chat log admin state
  chatLogUsers: [],
  chatLogUsersLoading: false,
  chatLogUsersError: null,
  chatLogConversation: [],
  chatLogConversationLoading: false,
  chatLogConversationError: null,
  totalMessages: 0,
  gdprNotice: '',
  gdprConsents: null,
  pagination: null,
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearCurrentConversation: state => {
      state.currentConversation = null
      state.messages = []
    },
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload
    },
  },
  extraReducers: builder => {
    builder
      // Fetch user conversations
      .addCase(fetchUserConversations.pending, state => {
        state.loading = true
      })
      .addCase(fetchUserConversations.fulfilled, (state, action) => {
        state.loading = false
        state.conversations = action.payload.data || []
      })
      .addCase(fetchUserConversations.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch conversation messages
      .addCase(fetchConversationMessages.pending, state => {
        state.loading = true
      })
      .addCase(fetchConversationMessages.fulfilled, (state, action) => {
        state.loading = false
        state.messages = action.payload.data?.messages || []
        state.currentConversation = action.payload.data?.conversation
      })
      .addCase(fetchConversationMessages.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Send chat message
      .addCase(sendChatMessage.pending, state => {
        state.loading = true
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.data) {
          state.messages.push(action.payload.data)
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Mark as read
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        const conversationId = action.meta.arg
        const conversation = state.conversations.find(c => c._id === conversationId)
        if (conversation) {
          conversation.unreadCount = 0
        }
      })

      // ADMIN CHAT LOG REDUCERS

      // Fetch chat log users
      .addCase(fetchChatLogUsers.pending, state => {
        state.chatLogUsersLoading = true
        state.chatLogUsersError = null
      })
      .addCase(fetchChatLogUsers.fulfilled, (state, action) => {
        state.chatLogUsersLoading = false
        state.chatLogUsers = action.payload.data?.users || action.payload.users || []
        state.pagination = action.payload.data?.pagination || action.payload.pagination || null
      })
      .addCase(fetchChatLogUsers.rejected, (state, action) => {
        state.chatLogUsersLoading = false
        state.chatLogUsersError = action.payload
      })

      // Load chat conversation
      .addCase(loadChatConversation.pending, state => {
        state.chatLogConversationLoading = true
        state.chatLogConversationError = null
      })
      .addCase(loadChatConversation.fulfilled, (state, action) => {
        state.chatLogConversationLoading = false
        state.chatLogConversation = action.payload.data?.conversation || action.payload.conversation || []
        state.totalMessages = action.payload.data?.totalMessages || action.payload.totalMessages || 0
        state.gdprNotice =
          action.payload.data?.gdprNotice ||
          action.payload.gdprNotice ||
          'This conversation data is accessed for support and compliance purposes.'
      })
      .addCase(loadChatConversation.rejected, (state, action) => {
        state.chatLogConversationLoading = false
        state.chatLogConversationError = action.payload
      })

      // Export chat logs
      .addCase(exportChatLogs.pending, state => {
        state.loading = true
      })
      .addCase(exportChatLogs.fulfilled, state => {
        state.loading = false
      })
      .addCase(exportChatLogs.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Get GDPR consents
      .addCase(getGDPRConsents.fulfilled, (state, action) => {
        state.gdprConsents = action.payload.data || action.payload
      })

      // Update GDPR consents
      .addCase(updateGDPRConsents.fulfilled, (state, action) => {
        state.gdprConsents = action.payload.data || action.payload
      })
  },
})

export const { clearCurrentConversation, setCurrentConversation } = chatSlice.actions
export default chatSlice.reducer
